import { Tabs } from '@/ui-kit'
import { formatNumber } from '@/utils/formatNumber'
import { Trans, useLingui } from '@lingui/react/macro'
import { useState } from 'react'
import { useStakingRewardsConfig } from '../../api/useStakingRewardsConfig'
import { useValidatorAPYContext } from '../../providers/ValidatorAPYContext'

type ProjectedEarningsProps = {
  stakeAmount: string
}

type EarningsPeriod = '3month' | '6month' | 'year'

const PERIOD_FACTOR: Record<EarningsPeriod, number> = {
  '3month': 3 / 12,
  '6month': 6 / 12,
  year: 1
}

export const ProjectedEarnings = ({ stakeAmount }: ProjectedEarningsProps) => {
  const { t } = useLingui()
  const [period, setPeriod] = useState<EarningsPeriod>('year')
  const { apy } = useValidatorAPYContext()
  const { base, rate, isLoading: isConfigLoading, minStakeSol } = useStakingRewardsConfig()
  const amount = Number.parseFloat(stakeAmount) || 0
  const yearlyRewards = amount > 0 ? (amount * apy) / 100 : 0
  const yearlyAirdrops = amount > 0 ? (amount * rate + base) * 182 : 0
  const factor = PERIOD_FACTOR[period]
  const projectedRewards = yearlyRewards * factor
  const projectedAirdrops = yearlyAirdrops * factor

  const earningsTabs = [
    { value: '3month' as const, title: t`3 months` },
    { value: '6month' as const, title: t`6 months` },
    { value: 'year' as const, title: t`Yearly` }
  ]

  const rewardsLabel = {
    '3month': t`Projected Rewards for 3 months`,
    '6month': t`Projected Rewards for 6 months`,
    year: t`Yearly Projected Rewards`
  }[period]

  const airdropsLabel = {
    '3month': t`Projected Airdrops Points for 3 months`,
    '6month': t`Projected Airdrops Points for 6 months`,
    year: t`Yearly Projected Airdrops Points`
  }[period]

  return (
    <div className="flex flex-col">
      <div className="max-mobile:max-w-full mb-3 max-w-max">
        <Tabs<{ value: EarningsPeriod; title: string }>
          className="h-10"
          size="small"
          tabs={earningsTabs}
          value={period}
          onChange={(tab) => setPeriod(tab.value)}
        />
      </div>
      <div className="flex flex-col justify-between gap-2 py-3">
        <span className="text-body-3 text-gray-90">{rewardsLabel}</span>
        <span className="font-semibold">
          {amount > 0 ? (projectedRewards < 0.01 ? '<0.01' : projectedRewards.toFixed(2)) : '0'} SOL
        </span>
      </div>
      <div className="flex flex-col justify-between gap-2 border-t border-gray-100 py-3">
        <span className="text-body-3 text-gray-90">{airdropsLabel}</span>
        <span className="font-semibold">
          {amount === 0 || amount < minStakeSol ? '0' : isConfigLoading ? '-' : formatNumber(projectedAirdrops, 0)}{' '}
          <Trans>$DATA Points</Trans>
        </span>
      </div>
    </div>
  )
}
